import { useLocation, matchPath } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import Dialog from '@mui/material/Dialog';
import { Colors, Sizes } from 'styles';
import GenericSnackBar from 'common/GenericSnackBar/GenericSnackBar';
import type { GenericSnackBarProps } from 'common/GenericSnackBar/GenericSnackBar';
import AccountBox from 'common/Account';
import { ReactComponent as MonitorSettingsIcon } from 'assets/monitorSettingsIcon.svg';

import { AppRoutePath, CHROME_EXTENSION_INSTALL_PAGE } from 'config/constants';
import { MenuProfilePopper } from 'common/ui/MenuProfilePopper';
import { Stack, Spacer } from 'common/ui/Layout';
import { useOpenTabs } from 'hooks/useOpenTabs';
import { transparentize } from 'color2k';
import { MenuItem } from './MenuItem';
import { Avatar, BackdropBlur, SironaLogo, ToggleableArea, floatingCss, surface } from './styles';
import { toggleModal } from 'common/WorkspaceManager/WorkspaceManager';
import { focusAllEvent, dispatchExtensionEvent } from 'domains/extension/extensionEventCreators';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { PWATitleBar } from '../ui/PWATitleBar';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { telemetryContextFor } from 'modules/Apollo/types';
import { matchPaths } from 'utils/matchPaths';
import { PageTypes } from 'utils/pageTypes';
import { NavMenuItemConfig, useGenerateNavBarMenuConfig } from './useGenerateNavBarMenuConfig';
import WorkspaceLayoutMenu from 'common/WorkspaceLayoutManager/components/WorkspaceLayoutMenu';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import Tooltip from 'common/ui/Tooltip';

export function Navbar({
  toggleable = false,
  floating = false,
}: {
  toggleable?: boolean;
  floating?: boolean;
}): React.ReactElement {
  const { pathname } = useLocation();
  const [accountBoxOpen, setAccountBoxOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(!toggleable);
  const [isWorkspaceLayoutV2Enabled] = useFeatureFlagEnabled(FF.WORKSPACE_LAYOUT_V2);
  const [workspaceLayoutAnchorEl, setWorkspaceLayoutAnchorEl] = useState<HTMLElement | null>(null);

  const isWorkspaceLayoutMenuOpen = Boolean(workspaceLayoutAnchorEl);
  const {
    openedViewerIds,
    isReporterOpen,
    isWorklistOpen,
    isSearchOpen,
    isJacketOpen,
    isAISandboxOpen,
  } = useOpenTabs();

  const [snackbarMessage, setSnackbarMessage] = useState<{
    message: React.ReactNode;
    severity: GenericSnackBarProps['severity'];
  }>({ message: null, severity: 'success' });

  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const me = data?.me;
  const currUserName = me ? `${me.firstName} ${me.lastName}` : null;
  const zendeskWindowOptions = [
    'https://static.zdassets.com/web_widget/latest/liveChat.html?v=10#key=sironamedical.zendesk.com',
    '',
    'height=600,width=500',
  ];

  const getIsRelatedWindowOpen = useCallback(
    ({ basePath, viewerIndex = '0' }: { basePath: string; viewerIndex?: string }) => {
      const checks = [
        () => openedViewerIds.has(viewerIndex) && matchPaths(PageTypes.VIEWER, basePath),
        () => isReporterOpen && matchPaths(PageTypes.REPORTER, basePath, { end: false }),
        () => isWorklistOpen && matchPath(AppRoutePath.WORKLIST, basePath),
        () => isSearchOpen && matchPath(AppRoutePath.WORKLIST_SEARCH, basePath),
        () =>
          isJacketOpen &&
          (matchPaths(PageTypes.PATIENT_JACKET, basePath) ||
            matchPath(AppRoutePath.PATIENT_JACKET, basePath)),
        () => isAISandboxOpen && matchPaths(PageTypes.AI_SANDBOX, basePath),
      ];
      return checks.some((fn) => fn());
    },
    [isAISandboxOpen, isJacketOpen, isReporterOpen, isSearchOpen, isWorklistOpen, openedViewerIds]
  );

  const menuConfig = useGenerateNavBarMenuConfig({ context: APOLLO_QUERY_CONTEXT });

  const menuItems: React.ReactElement[] = useMemo(
    () =>
      menuConfig.map(
        ({
          label,
          openPath,
          basePath,
          children,
          dataTestId,
          viewerIndex = '0',
          isExternal,
          disabled,
          tooltip,
          onClick,
        }: NavMenuItemConfig) => {
          const isRelatedWindowOpen = getIsRelatedWindowOpen({ basePath, viewerIndex });

          return (
            <MenuItem
              key={label}
              selected={
                matchPath(
                  { path: basePath, end: false },
                  pathname === '/' ? '/worklist' : pathname
                ) != null
              }
              isRelatedWindowOpen={isRelatedWindowOpen}
              label={label}
              to={onClick ? '#' : (openPath ?? '#')}
              onClick={onClick}
              aria-label={label}
              data-testid={dataTestId}
              setSnackbarMessage={setSnackbarMessage}
              isReporterOpen={isReporterOpen}
              isAISandboxOpen={isAISandboxOpen}
              isWorklistOpen={isWorklistOpen}
              isSearchOpen={isSearchOpen}
              isJacketOpen={isJacketOpen}
              openedViewerIds={openedViewerIds}
              viewerIndex={viewerIndex}
              isExternal={isExternal}
              disabled={disabled}
              tooltip={tooltip}
            >
              {children}
            </MenuItem>
          );
        }
      ),
    [
      menuConfig,
      getIsRelatedWindowOpen,
      pathname,
      isReporterOpen,
      isAISandboxOpen,
      isWorklistOpen,
      isSearchOpen,
      isJacketOpen,
      openedViewerIds,
    ]
  );

  return (
    <>
      <Stack css={floating ? floatingCss : 'app-region: drag;'} vertical alignX="center" stretchY>
        <Stack
          alignX="center"
          css={`
            ${surface};
            position: relative;
            z-index: 1;
            height: ${Sizes.toolbarHeight};
            margin-top: env(titlebar-area-height, 0);
          `}
        >
          <IconButton
            disabled={!toggleable}
            onClick={() => toggleable && setIsOpen((isOpen) => !isOpen)}
            size="large"
          >
            <SironaLogo />
          </IconButton>
        </Stack>

        <ToggleableArea isOpen={isOpen} css="min-height: 0;">
          <Stack
            vertical
            alignX="center"
            alignY="start"
            stretchY
            css={`
              padding-bottom: 2rem;
              min-height: 0;
              overflow: auto;
              ::-webkit-scrollbar {
                width: 0;
              }
              scrollbar-width: none;
            `}
          >
            {menuItems}
            <Spacer />
            <Stack vertical alignX="center" css="app-region: no-drag;">
              <Tooltip content="Bring all Sirona windows to front" placement="right">
                <IconButton
                  onClick={() => {
                    if (isWorkspaceManagerAvailable()) {
                      dispatchExtensionEvent(focusAllEvent());
                    } else {
                      window.open(CHROME_EXTENSION_INSTALL_PAGE, '_blank');
                    }
                  }}
                  size="large"
                  title="Bring all Sirona windows to front"
                  aria-label="Bring all Sirona windows to front"
                >
                  <FilterNoneIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
              <Tooltip content="Workspace Layout Manager" placement="right">
                <IconButton
                  onClick={(event) => {
                    if (isWorkspaceManagerAvailable()) {
                      if (isWorkspaceLayoutV2Enabled) {
                        setWorkspaceLayoutAnchorEl(event.currentTarget);
                      } else {
                        toggleModal();
                      }
                    } else {
                      window.open(CHROME_EXTENSION_INSTALL_PAGE, '_blank');
                    }
                  }}
                  size="large"
                >
                  <MonitorSettingsIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                id="intercom-button"
                onClick={(e) => {
                  window.open(...zendeskWindowOptions);
                }}
                size="large"
              >
                <HelpIcon />
              </IconButton>
              <MenuProfilePopper
                displayName={currUserName}
                email={me?.email ?? null}
                clinicName={me?.clinic?.name ?? null}
                clinicSmid={me?.clinic?.smid ?? null}
                onSettingClick={() => setAccountBoxOpen(true)}
                onLogoutClick={() => (window.location.href = '/logout')}
                strategy="fixed"
              >
                <IconButton size="small" data-testid="avatar-button">
                  <Avatar name={currUserName} />
                </IconButton>
              </MenuProfilePopper>
            </Stack>
          </Stack>
        </ToggleableArea>
      </Stack>
      <GenericSnackBar
        open={snackbarMessage.message != null}
        handleClose={(_, reason) => {
          reason !== 'clickaway' && setSnackbarMessage((s) => ({ ...s, message: null }));
        }}
        msg={snackbarMessage.message ?? ''}
        severity={snackbarMessage.severity}
        disableWindowBlurListener={true}
      />
      {/*
        Set `disableEnforceFocus` on the MUI dialog as it "steals" focus
        from inputs when a plugin (like 1password) is used.
        https://sironamedical.atlassian.net/browse/EN-4033
      */}
      <Dialog
        open={accountBoxOpen}
        onClose={() => setAccountBoxOpen(false)}
        maxWidth={false}
        disableEnforceFocus
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: transparentize(Colors.gray1, 0.3),
          },
        }}
      >
        <PWATitleBar />
        <BackdropBlur />
        <AccountBox onClose={() => setAccountBoxOpen(false)} />
      </Dialog>
      {isWorkspaceLayoutV2Enabled && (
        <WorkspaceLayoutMenu
          isOpen={isWorkspaceLayoutMenuOpen}
          anchorEl={workspaceLayoutAnchorEl}
          onClose={() => setWorkspaceLayoutAnchorEl(null)}
        />
      )}
    </>
  );
}

Navbar.displayName = 'Navbar';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(Navbar);
